import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { catalogStore } from '../../stores';
import constant from '../../utils/constant';
import { amountFormatter } from '../../utils/utils';
import { StyleCatalogController } from '../../controllers';
import { MimaToastUtil } from '../../components';
import useGetSubdomain from '../useGetSubdomain';

const useCart = (fashCart, setFashCart) => {
	const navigate = useNavigate();

	const { newShippingInfo } = catalogStore;

	// const [fashCart, setFashCart] = useState([]);
	const [quantities, setQuantities] = useState([]);

	const { subdomain } = useGetSubdomain();

	useEffect(() => {
		const storedFashCart = JSON.parse(localStorage.getItem('fashCart')) || [];
		setQuantities(storedFashCart?.map(cart => cart?.quantity));
		setFashCart(storedFashCart);

		// Stored prices may be out of date, so re-price against the catalogue
		const refreshPrices = async () => {
			const { cart, changed } = await StyleCatalogController.refreshCartPrices(
				storedFashCart,
				subdomain
			);
			if (!changed) return;

			localStorage.setItem('fashCart', JSON.stringify(cart));
			setQuantities(cart.map(item => item?.quantity));
			setFashCart(cart);
			MimaToastUtil.success({
				message: 'Some items in your cart have been updated to current prices',
			});
		};

		if (storedFashCart.length > 0) refreshPrices();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// const [loading, setLoading] = useState({});

	// const asyncDebounce = useAsyncDebounce(updateCartQuantity, 1000);

	const updateCartQuantityHandler = async (cart, newQuantities, index) => {
		const updatedCart = fashCart.map((item, index) => {
			return {
				...item,
				quantity: newQuantities[index],
				amount: newQuantities[index] * item.amountPerItem,
			};
		});
		setFashCart(updatedCart);
		localStorage.setItem('fashCart', JSON.stringify(updatedCart));
	};

	const handleQtyChange = (index, event, cart) => {
		const newQuantities = [...quantities];
		newQuantities[index] = parseInt(event.target.value || '0');
		setQuantities(newQuantities);
		updateCartQuantityHandler(cart, newQuantities, index);
	};

	const handleIncrement = (index, cart) => {
		const newQuantities = [...quantities];
		newQuantities[index]++;
		setQuantities(newQuantities);

		updateCartQuantityHandler(cart, newQuantities, index);
	};

	const handleDecrement = (index, cart) => {
		const newQuantities = [...quantities];

		if (newQuantities[index] === 1) {
			return removeItemHandler(index);
		}

		if (newQuantities[index] > 1) {
			newQuantities[index]--;
			setQuantities(newQuantities);
		}

		updateCartQuantityHandler(cart, newQuantities, index);
	};

	const removeItemHandler = index => {
		// setLoading(prevState => ({ ...prevState, [id]: true }));
		// await removeFromCart(id);
		// setLoading(prevState => ({ ...prevState, [id]: false }));
		const newQuantities = [...quantities];
		const updatedCartItems = [...fashCart];

		updatedCartItems.splice(index, 1);
		newQuantities.splice(index, 1);

		setFashCart(updatedCartItems);
		setQuantities(newQuantities);

		localStorage.setItem('fashCart', JSON.stringify(updatedCartItems));
		setFashCart(updatedCartItems);
	};

	const calcSubtotalPrice = items => {
		return items?.reduce((totalPrice, cart) => {
			const { amountPerItem, quantity } = cart;
			return totalPrice + amountPerItem * quantity;
		}, 0);
	};

	const checkoutHandler = () => {
		catalogStore.setCatalogOrderInfo(fashCart);

		window.sendTrackingEvent(constant.events.initiate_checkout, {
			content_ids: fashCart.map(item => item.styleId),
			content_type: 'product',
			value: subTotalPrice,
			currency: fashCart[0]?.currencyCode,
			num_items: fashCart.length,
		});
		navigate(constant.Routes.PaymentCheckout);
	};

	const subTotalPrice = useMemo(() => {
		const subTotalPrice =
			fashCart?.length > 0 ? calcSubtotalPrice(fashCart) : 0;

		return subTotalPrice;
	}, [fashCart]);

	const paystackCharge = useMemo(() => {
		return subTotalPrice < 2500
			? subTotalPrice * 0.015
			: subTotalPrice * 0.015 + 100 < 2000
				? subTotalPrice * 0.015 + 100
				: 2000;
	}, [subTotalPrice]);

	const preChargesTotal = useMemo(() => {
		const totalWithShipping =
			calcSubtotalPrice(fashCart) + newShippingInfo?.shippingFee;
		const totalWithoutShipping = calcSubtotalPrice(fashCart);

		const subTotalPrice =
			fashCart?.length > 0 &&
			totalWithoutShipping > newShippingInfo?.freeShippingForOrderAbove &&
			newShippingInfo?.freeShippingForOrderAbove > 0
				? totalWithoutShipping
				: fashCart?.length > 0
					? totalWithShipping
					: 0;

		return subTotalPrice;
	}, [fashCart, newShippingInfo]);

	const emptyCartText = 'Your cart is currently empty';

	const emptyButtonHandler = route => {
		navigate(route);
	};

	const totalPrice = useMemo(() => {
		// if (fashCart?.[0]?.currencyCode === 'NGN') {
		// 	return amountFormatter(fashCart?.[0]?.currencyCode || 'NGN').format(
		// 		preChargesTotal + paystackCharge
		// 	);
		// }

		// return amountFormatter(fashCart?.[0]?.currencyCode || 'USD').format(
		// 	preChargesTotal
		// );
		return amountFormatter(fashCart?.[0]?.currencyCode || 'NGN').format(
			subTotalPrice
		);
	}, [subTotalPrice, fashCart]);

	//Event tracker start
	useEffect(() => {
		window.sendTrackingEvent(constant.events.page_visit, {
			page_type: 'cart page',
		});
	}, []);
	//Event tracker end

	return {
		fashCart,
		handleQtyChange,
		handleDecrement,
		removeItemHandler,
		handleIncrement,
		quantities,
		calcSubtotalPrice,
		checkoutHandler,
		subTotalPrice,
		preChargesTotal,
		paystackCharge,
		emptyCartText,
		emptyButtonHandler,
		totalPrice,
	};
};

export default useCart;
